import { gql, useQuery } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import { stringifyUrl } from 'query-string';
import { T, always, cond, equals } from 'ramda';
import React, { FC, useState } from 'react';
import { useAsync } from 'react-use';
import styled from 'styled-components';

import Skeleton from '@ui/atoms/Skeleton';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import IFrameContainer from '@ui/molecules/IFrameContainer';
import RoundedCard from '@ui/molecules/RoundedCard';

import BookingUnavailable from '@ui/organisms/BookingUnavailable/BookingUnavailable.component';

import useFlowData from '@hooks/useFlowData';

import { getServiceDisplayName } from '@helpers/Appointments';

import { interpolate, keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { exists } from '@utils/general';
import { getStateName } from '@utils/intake/stateRequirements';

import { useAuth } from '@providers/AuthProvider';

import { FormData } from '@folx-types/flow';

const BOOKING_ERROR = 'BOOKING_ERROR';
const SERVICE_DURATION_ERROR = 'SERVICE_DURATION_ERROR';

const BookingContainer = styled.div`
	height: 78rem;
`;

interface IMember {
	member: {
		id: string;
		affirmingFirstName: string;
		affirmingLastName: string;
		legalFirstName: string;
		legalLastName: string;
		email: string;
		stateOfResidence: string;
	};
}
export type ServiceType =
	| 'vc'
	| 'e'
	| 't'
	| 'ed'
	| 'prep'
	| 'inj'
	| 'mht'
	| 'mhm'
	| 'mhl'
	| 'transfer';
export type ServiceDuration = 'extended' | 'brief' | 'default' | undefined;

export const durationRequiredServiceTypes: ServiceType[] = ['e', 't', 'vc'];

interface NewAppointmentBookingProps {
	loading?: boolean;
	serviceType: ServiceType;
	serviceDuration?: ServiceDuration;
	serviceDurationError?: Error;
	serviceVersion?: number;
	locationType?: string;
	overrideBookingURL?: string;
	defaultVisitDuration?: ServiceDuration;
	isMhm?: boolean;
}

export function cogsworthUrlQuery(
	member: { id: string; name: string; email: string },
	serviceType: string,
	isMhm = false
) {
	return {
		'answer[name]': member.name,
		'answer[email]': member.email,
		'answer[text_1]': new URL(`/public/chart/${member.id}`, location.href).href,
		'answer[text_2]': new URL(`/${isMhm ? 'mhm' : serviceType}`, location.href).href,
		// This is set by the backend, but we have to give it a non-empty value here to prevent CW from asking the member to fill it out
		'answer[google-maps-address_1]': '—',
	};
}

async function fetchCogsworthUrl(
	serviceType: ServiceType,
	serviceDuration: ServiceDuration,
	serviceVersion: number,
	locationType: string,
	userId: string,
	authorizationToken: string,
	memberData: IMember,
	onboardingData: FormData,
	overrideBookingURL?: string,
	defaultVisitDuration?: ServiceDuration,
	isMhm?: boolean
) {
	const customerName = `${
		memberData?.member.affirmingFirstName ??
		memberData?.member.legalFirstName ??
		onboardingData?.preferredFirstName ??
		onboardingData?.legalFirstName
	} ${
		memberData?.member.affirmingLastName ??
		memberData?.member.legalLastName ??
		onboardingData?.preferredLastName ??
		onboardingData?.legalLastName
	}`;

	serviceDuration = serviceDuration === 'default' ? undefined : serviceDuration;
	defaultVisitDuration = defaultVisitDuration === 'default' ? undefined : serviceDuration;

	try {
		const response = await axios.get<{ bookingUrl: string } | { bookingUrls: [] }>(
			stringifyUrl({
				url: `${ApiEndpoints.cogsworth}/get-booking-url/${serviceType}`,
				query: {
					duration: serviceDuration,
					version: serviceVersion,
					'location-type': locationType,
					...(defaultVisitDuration === 'brief' &&
						serviceType === 'mhm' && { recurrence: 'follow-up' }),
				},
			}),
			{
				headers: {
					authorizationToken,
				},
				responseType: 'json',
			}
		);

		let bookingURL: string;
		if (locationType === 'irl') {
			/** IMPORTANT
			 * In the future we're going to have multiple booking locations
			 * per state. So this will need to be refactored to not pick
			 * the first available response
			 */
			const bookingURLs: string[] = (response.data as { bookingUrls: [] }).bookingUrls;
			bookingURL = bookingURLs[0];
		} else {
			bookingURL = (response.data as { bookingUrl: string }).bookingUrl;
		}

		return stringifyUrl({
			url: overrideBookingURL ? overrideBookingURL : bookingURL,
			query: cogsworthUrlQuery(
				{ id: userId, name: customerName, email: memberData?.member.email ?? '' },
				serviceType,
				isMhm
			),
		});
	} catch (error) {
		console.warn('Error fetching Cogsworth booking URL: ', error);
		return BOOKING_ERROR;
	}
}

const NewAppointmentBooking: FC<NewAppointmentBookingProps> = (props) => {
	const {
		loading,
		serviceType,
		serviceDuration,
		serviceDurationError,
		serviceVersion,
		locationType,
		overrideBookingURL,
		defaultVisitDuration,
		isMhm,
	} = {
		serviceVersion: 1,
		...props,
	};

	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "new-appointment-booking" }) {
				booking_error
				booking_error_html
				service_duration_error_html
			}
		}
	`);

	const refinedContent: {
		bookingError: string;
		bookingErrorHtml: string;
		serviceDurationErrorHtml: string;
	} = keysToCamel(pageContent) as {
		bookingError: string;
		bookingErrorHtml: string;
		serviceDurationErrorHtml: string;
	};

	const MEMBER_QUERY = gql`
		query member($memberId: UUID!) {
			member(id: $memberId) {
				id
				affirmingFirstName
				affirmingLastName
				legalFirstName
				legalLastName
				email
				stateOfResidence
			}
		}
	`;

	const [cogsworthUrl, setCogsworthUrl] = useState('');

	const { userId, getAuthToken } = useAuth();
	const { formData: onboardingData, loaded: onboardingLoaded } = useFlowData('user_onboarding');
	const { data: memberData, loading: memberLoading } = useQuery<IMember, { memberId: string }>(
		MEMBER_QUERY,
		{
			variables: {
				memberId: userId,
			},
			skip: !userId,
		}
	);

	const { loading: cogsworthUrlLoading } = useAsync(async () => {
		if (exists(cogsworthUrl)) {
			return;
		}

		if (
			!serviceDuration &&
			durationRequiredServiceTypes.includes(serviceType) &&
			locationType != 'irl'
		) {
			console.error('No duration specified');
			datadogRum.addError('No duration specified', {
				serviceDuration,
				serviceDurationError,
				serviceType,
				defaultVisitDuration,
				locationType,
			});
			return setCogsworthUrl(SERVICE_DURATION_ERROR);
		}

		if (!loading && !memberLoading && onboardingLoaded && memberData && onboardingData) {
			const result = await fetchCogsworthUrl(
				serviceType,
				serviceDuration,
				serviceVersion,
				locationType ?? 'virtual',
				userId,
				await getAuthToken(),
				memberData,
				onboardingData,
				overrideBookingURL,
				defaultVisitDuration,
				isMhm
			);
			return setCogsworthUrl(result);
		}
	}, [loading, memberLoading, onboardingLoaded, memberData, onboardingData]);

	const hasError = cogsworthUrl == BOOKING_ERROR || cogsworthUrl == SERVICE_DURATION_ERROR;
	const appointmentErrorText = cond([
		[equals(BOOKING_ERROR), always(refinedContent.bookingErrorHtml)],
		[equals(SERVICE_DURATION_ERROR), always(refinedContent.serviceDurationErrorHtml)],
		[T, always('')],
	])(cogsworthUrl);

	return (
		<BookingUnavailable>
			<BookingContainer>
				{cogsworthUrl && !cogsworthUrlLoading && !loading ? (
					hasError ? (
						<RoundedCard bgColor="cardRed">
							<WYSIWYGContent
								dangerouslySetInnerHTML={{
									__html: interpolate(appointmentErrorText, {
										state: getStateName(memberData?.member.stateOfResidence),
										service: getServiceDisplayName(serviceType.toLocaleUpperCase()),
									}),
								}}
							/>
						</RoundedCard>
					) : (
						<IFrameContainer iFrameStyle={{ height: '100%', width: '100%' }} url={cogsworthUrl} />
					)
				) : (
					<Skeleton fullHeight fullWidth structure="single" />
				)}
			</BookingContainer>
		</BookingUnavailable>
	);
};

export default NewAppointmentBooking;
